import { deepClone } from '@/_utils/deep-clone';
import { AuthService } from '@/auth/auth.service';
import { AvailabilityKind } from '@/common/enums/AvailabilityKind';
import { OfferAddOrConfigTypes, OfferChangeTypes } from '@/common/enums/OfferChangeType';
import { PaymentTermCountKind } from '@/common/enums/PaymentTermCountKind';
import { Status } from '@/common/enums/Status';
import { TransportKind } from '@/common/enums/TransportKind';
import { InfoboxService } from '@/common/infobox/infobox.service';
import { RequestTrackerInterceptor } from '@/common/interceptors/request-tracker.interceptor';
import { LoaderService } from '@/common/loader/loader.service';
import { OskarOfferUrlBuilder } from '@/common/oskar-offer-url-builder';
import { RequestTrackerService } from '@/common/request-tracker.service';
import {
	ActivePriceSettingBoard,
	ActivePriceSettingOptions,
	PriceSettings,
	SellAccommodation,
} from '@/reservation/reservation-summary/reservation-summary.model';
import { ReservationSummaryApiService } from '@/reservation/reservation-summary/services/reservation-summary-api.service';
import { ReservationSummaryStoreService } from '@/reservation/reservation-summary/services/reservation-summary-store.service';
import {
	AvailabilityCheck,
	AvailabilityTransport,
	ChangeOfferSelected,
	ChangeOfferStop,
	DateRange,
	NO_TRANSPORT_NAME,
	OWN_TRANSPORT_NAME,
	OfferChangeDialog,
	OfferChangeService,
	OfferChangeSlot,
	OfferChangeStop,
	FrontendOfferService,
	OfferService,
	ProductToServiceFilter,
	Promotion,
	ReservationOfferSend,
	SlotOfferSend,
	Stop,
} from '@/reservation/reservation.model';
import { ServiceCountingMethods, ServiceDependsOn } from '@/services/service.model';
import { DialogRef } from '@angular/cdk/dialog';
import { DatePipe } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpParams } from '@angular/common/http';
import { Component, DestroyRef, Inject, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import {
	BehaviorSubject,
	Observable,
	Subscription,
	catchError,
	combineLatest,
	filter,
	finalize,
	forkJoin,
	from,
	map,
	of,
	switchMap,
	take,
} from 'rxjs';
import { ConfirmChangeDialogComponent } from './confirm-change-dialog/confirm-change-dialog.component';
import { deepCompare } from '@/_utils/deep-compare';
import { Insurance } from '@/codebook/insurer/_types/insurer.type';
import { Permissions } from '@/common/enums/Permissions';
import { PriceSettingServicesNames } from '../reservation-summary-accomodation-offer-rooms-dialog.component';
import { StatusCode } from '@/common/enums/StatusCode';
import { ForceTransportDialogComponent } from './force-transport-dialog/force-transport-dialog.component';
import { calcPercentagePromotionValue, shouldIncludeTransportInPromo } from '../../_utils/offer-promotion-pricing.util';

export const CANCELLATION_INSURANCE_NAME = 'Ubezpieczenie od rezygnacji';

enum Transport {
	RETURN = 'return',
	DEPARTURE = 'departure',
}

interface DialogData {
	currentOffer: OfferChangeDialog;
	selectedOffer: ChangeOfferSelected;
	surchargeService: FrontendOfferService;
	reservationStatus: Status;
}

@Component({
	selector: 'app-reservation-summary-accomodation-offer-rooms-dialog-offer-change-dialog',
	templateUrl: './reservation-summary-accomodation-offer-rooms-dialog-offer-change-dialog.component.html',
	styleUrls: ['./reservation-summary-accomodation-offer-rooms-dialog-offer-change-dialog.component.scss'],
	providers: [
		DatePipe,
		OskarOfferUrlBuilder,
		{
			provide: HTTP_INTERCEPTORS,
			useClass: RequestTrackerInterceptor,
			multi: true,
		},
	],
})
export class ReservationSummaryAccomodationOfferRoomsDialogOfferChangeDialogComponent implements OnInit, OnDestroy {
	nights = '';
	stops: ChangeOfferStop[] = [];
	additionalRoomSizeCharge = 0;
	promotions: Promotion[];
	readonly Status = Status;
	readonly OfferChangeTypes = OfferChangeTypes;
	readonly PaymentTermCountKind = PaymentTermCountKind;
	loading$ = new BehaviorSubject<boolean>(true);
	offerServices: FrontendOfferService[] = [];
	offerOrConfigView = true;
	isRequestPending = false;
	sellAccommodations$: Observable<SellAccommodation[]> = of([]);
	priceSettings$: Observable<PriceSettings> = of(null);
	priceSettingsOptions$ = new BehaviorSubject<ActivePriceSettingOptions[]>([]);
	boards$: Observable<ActivePriceSettingBoard[]> = of([]);
	readonly rowNumber = 7;
	readonly OWN_TRANSPORT_NAME = OWN_TRANSPORT_NAME;
	readonly NO_TRANSPORT_NAME = NO_TRANSPORT_NAME;

	readonly displayChangedTransportDepartureMessage = signal(false);
	readonly insurances = signal<Insurance[]>([]);
	readonly insurancePrice = signal(0);

	private hasTransportChanged = false;
	private reservationId: number = null;
	private reservationSubscription: Subscription;
	private offerServiceSubscription: Subscription;

	private readonly infoboxService = inject(InfoboxService);
	private readonly requestTrackerService = inject(RequestTrackerService);

	constructor(
		@Inject(MAT_DIALOG_DATA)
		public data: DialogData,
		private readonly store: ReservationSummaryStoreService,
		private readonly auth: AuthService,
		private readonly dialog: MatDialog,
		private readonly reservationSummaryApiService: ReservationSummaryApiService,
		private readonly dialogRef: DialogRef<ReservationSummaryAccomodationOfferRoomsDialogOfferChangeDialogComponent>,
		private readonly infobox: InfoboxService,
		private readonly loader: LoaderService,
		private readonly datePipe: DatePipe,
		private readonly urlBuilder: OskarOfferUrlBuilder,
	) {}

	private readonly destroyRef = inject(DestroyRef);

	ngOnInit(): void {
		this.reservationSummaryApiService
			.getOfferPriceSettings(this.data.selectedOffer.term)
			.pipe(map((priceSettings) => priceSettings.calculatedPriceSetting))
			.subscribe((value) => {
				this.priceSettings$ = of(value);
				this.boards$ = of(value?.activePriceSettingBoards ?? []);
				this.priceSettingsOptions$.next(value?.activePriceSettingOptions ?? []);
				this.subscribeForData();
				this.sellAccommodations$ = this.reservationSummaryApiService
					.getAccommodation(this.data.selectedOffer.term)
					.pipe(
						map((accommodations) =>
							accommodations.filter((accommodation) => {
								const accommodationParticipants = accommodation.adults + accommodation.children;
								const selectedOfferParticipants =
									this.data.selectedOffer.participants.adults +
									this.data.selectedOffer.participants.children;

								return (
									accommodationParticipants >= selectedOfferParticipants ||
									accommodationParticipants === selectedOfferParticipants
								);
							}),
						),
					);
			});

		this.assignInsurances();

		this.requestTrackerService
			.getRequestCountObservable()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((count) => (this.isRequestPending = count > 0));
		this.offerOrConfigView = this.isOfferAddOrConfigView(this.data.currentOffer.type);
		const { getOfferService } = this.reservationSummaryApiService.actions();
		const { reservation } = this.store.getters();
		this.reservationSubscription = reservation().subscribe((val) => (this.reservationId = val?.id));
		const originalDateObj = new Date(this.data.selectedOffer?.dateRange.lower);
		if (!this.data.selectedOffer.roomType.name) {
			originalDateObj.setDate(originalDateObj.getDate() - 1);
		}
		const convertedLowerDate = this.datePipe.transform(originalDateObj, 'yyyy-MM-dd');

		const firstSlot = this.data.currentOffer.slot?.[0];
		const transportKindForServices =
			this.data.currentOffer.type === OfferChangeTypes.CONFIG
				? this.data.currentOffer.transportKind
				: this.data.selectedOffer.transportKind;
		const params = new HttpParams()
			.set('date_range', `${convertedLowerDate},${this.data.selectedOffer?.dateRange.upper}`)
			.set('products', String(this.data.selectedOffer!.product.id))
			.set('kinds', String(this.data.selectedOffer!.product.productKind.id))
			.appendAll({ transport: [String(transportKindForServices), 'A'] })
			.set('stop_id', String(firstSlot?.stopDeparture?.stop?.id ?? ''))
			.set('transport_departure_id', String(firstSlot?.stopDeparture?.transport ?? ''))
			.set('transport_return_id', String(firstSlot?.stopReturn?.transport ?? ''))
			.set('limit', '999');

		const queryString = `?${params.toString()}`;

		getOfferService(queryString);
		this.nights = this.translateNights();
	}

	ngOnDestroy(): void {
		this.reservationSubscription?.unsubscribe();
		this.offerServiceSubscription?.unsubscribe();
	}

	get disableInputState(): boolean {
		return !(
			this.data.currentOffer.type === OfferChangeTypes.CONFIG && this.auth.permissionsByCode()[Permissions.ADMIN]
		);
	}

	addService(i: number, selectedService: FrontendOfferService): void {
		if (!selectedService) return;
		const service = this.createOfferChangeServiceObj(selectedService, i);

		this.data.currentOffer.slot[i].slotServices = [...this.data.currentOffer?.slot[i].slotServices, service];
		this.data.currentOffer?.slot.forEach((item) => {
			item.selectedService = null;
			this.filterServices(this.offerServices, item);
		});
		this.calculateNewPrices(this.data.currentOffer?.slot[i]);
	}

	changeActiveStatus(slot: OfferChangeSlot, serviceIndex: number): void {
		slot.slotServices[serviceIndex].active = !slot.slotServices[serviceIndex].active;
		this.calculateNewPrices(slot);
	}

	convertPriceToGrosze(item: number): number {
		return item > 0 ? Math.ceil(item * 100) : Math.floor(item * 100);
	}

	openConfirmChangeDialog(): void {
		this.dialog
			.open(ConfirmChangeDialogComponent, {
				width: '450px',
				disableClose: true,
				data: this.additionalRoomSizeCharge * this.data.selectedOffer.nightsStay,
			})
			.afterClosed()
			.subscribe((result) => {
				if (result) this.checkTransportAndRoomAvailability();
			});
	}

	async checkTransportAndRoomAvailability(): Promise<void> {
		if (this.data.currentOffer.type === OfferChangeTypes.CONFIG) {
			this.confirmOfferChange();
			return;
		}

		try {
			this.loader.show();

			const { checkTransportAvailability, checkRoomAvailability } = this.reservationSummaryApiService.actions();

			const transportRequests: Promise<AvailabilityCheck>[] = this.uniqueStops.map((stop) => {
				const transport: AvailabilityTransport = {
					departureId: stop.transport.departureId,
					personCount: this.data.currentOffer.slot.length,
					returnId: stop.transport.returnId,
					stopId: stop.stop,
				};
				return checkTransportAvailability(transport, this.hasTransportChanged);
			});

			const roomRequest = checkRoomAvailability(this.data.selectedOffer, this.data.currentOffer);

			const requestsData = await Promise.all([...transportRequests, roomRequest]);

			const isTransportAvailable = requestsData
				.filter((v) => v.type === AvailabilityKind.TRANSPORT)
				.every((v) => v.available);

			const isRoomAvailable = requestsData.find((v) => v.type === AvailabilityKind.ROOM).available;

			if (isTransportAvailable && isRoomAvailable) {
				this.confirmOfferChange();
			} else {
				this.showAvailabilityWarningMessage(isRoomAvailable, isTransportAvailable);
			}
		} catch (error) {
			throw new Error(JSON.stringify(error));
		} finally {
			this.loader.hide();
		}
	}

	transportChange(): void {
		if (this.data.currentOffer.type === OfferChangeTypes.CONFIG) {
			this.hasTransportChanged = true;
		}
	}

	closeDialog(): void {
		this.dialogRef.close();
	}

	isOfferAddOrConfigView(type: string): boolean {
		return OfferAddOrConfigTypes.includes(type as OfferChangeTypes);
	}

	changeSlotActiveState(slot: OfferChangeSlot): void {
		slot.active = !slot.active;
		this.calculateNewPrices(slot);
		this.calculateRoomTypePrices();
	}

	selectPromotion(promotionId: number): void {
		const selectedPromotion = this.promotions?.find((item) => item.id === promotionId);
		this.data.selectedOffer.promotion = selectedPromotion || null;

		if (!promotionId) {
			this.data.currentOffer.slot.forEach((slot) => {
				slot.promotionValue = 0;
			});
		}
		this.data.selectedOffer.promotionValue = promotionId ? this.calculatePromotionValue(selectedPromotion) : 0;
		this.data.currentOffer.slot.forEach((slot) => {
			slot.newPrice = this.setSlotPrice(slot);
			this.calculateNewPrices(slot);
		});
	}

	calculateNewPrices(slot: OfferChangeSlot): void {
		this.moveCancellationInsuranceToTheEndOfArray(slot.slotServices, () => {
			const servicesPrice = this.calculateTotalSlotServicePrice(slot);
			slot.newTotalPrice =
				slot.newPrice + slot.newBoardPrice + slot.newRoomTypePrice + servicesPrice + slot.priceAdjustment;
			this.calculateOfferTotalPrice();
		});
	}

	calculateTransportPrices(event: string, slot: OfferChangeSlot, type: string): void {
		const selectedStop = () => {
			if (event === null) {
				return this.ownTransportStopFactory();
			} else {
				return this.stops.find((stop) => stop.name === event);
			}
		};

		if (type === Transport.RETURN) slot.selectedStopReturn = selectedStop();
		if (type === Transport.DEPARTURE) {
			slot.selectedStopDeparture = selectedStop();
			this.displayChangedTransportDepartureMessage.set(this.checkIfDepartureHasChanged());
		}

		if (this.data.selectedOffer.promotion?.id) {
			this.selectPromotion(this.data.selectedOffer.promotion.id);
			return;
		}

		slot.newPrice = this.setSlotPrice(slot);
		this.calculateNewPrices(slot);
	}

	calculateBoardPrices(boardName: string): void {
		this.boards$
			.pipe(
				take(1),
				map((boards) => boards.find((board) => board.board.name === boardName)),
				filter((board) => !!board),
			)
			.subscribe((board) => {
				this.data.selectedOffer.board = {
					...this.data.selectedOffer.board,
					name: board.board.name,
					id: board.board.id,
					price: board.price,
				};
				this.convertSlot();
				this.selectPromotion(this.data.selectedOffer.promotion?.id);
			});
	}

	calculateRoomTypePrices(roomTypeName?: string): void {
		combineLatest([
			this.sellAccommodations$,
			this.priceSettingsOptions$.pipe(
				map((options) => options.find((option) => option.priceOption.name === 'Dopłata do wolnego miejsca')),
			),
		])
			.pipe(take(1))
			.subscribe(([accommodations, surcharge]) => {
				const newAccommodation = accommodations.find(
					(accommodation) => accommodation.name === (roomTypeName ?? this.data.selectedOffer.roomType.name),
				);
				const oldAccommodation = accommodations.find(
					(accommodation) => accommodation.id === this.data.currentOffer.roomType.id,
				);

				const freePlaces =
					newAccommodation.adults +
					newAccommodation.children -
					this.data.currentOffer.slot.filter((s) => s.active).length;
				const nightsStay = this.data.selectedOffer?.nightsStay > 0 ? this.data.selectedOffer.nightsStay - 1 : 0;
				const surchargeValue = surcharge?.methodValue ?? 0;

				if (!roomTypeName) {
					this.data.selectedOffer.surchargeForExtraRoomSpace = freePlaces * surchargeValue * nightsStay;
					this.calculateOfferTotalPrice();
					return;
				}

				const difference = oldAccommodation
					? nightsStay * (newAccommodation.fee - oldAccommodation.fee)
					: nightsStay * newAccommodation.fee;

				this.data.selectedOffer.roomType = {
					id: newAccommodation.id,
					name: newAccommodation.name,
				};

				this.adjustOfferSlotsAfterAccommodationChange(freePlaces);

				this.data.currentOffer.slot?.map((s) => {
					s.newRoomTypePrice = difference;
				});

				this.convertSlot();
				this.selectPromotion(this.data.selectedOffer.promotion?.id);
			});
	}

	selectInsurance(insurance: Insurance): void {
		this.data.selectedOffer.insurance = insurance;
		this.setInsurancePrice(insurance);
	}

	get loading(): boolean {
		return this.loading$.getValue();
	}

	private adjustOfferSlotsAfterAccommodationChange(freePlaces: number): void {
		switch (true) {
			case freePlaces > 0: {
				const newSlots: OfferChangeSlot[] = new Array(freePlaces).fill({
					...this.data.currentOffer.slot[0],
					id: null,
					seatnumber: null,
					userData: null,
				});
				this.data.currentOffer.slot = [...this.data.currentOffer.slot, ...newSlots];
				break;
			}

			case freePlaces < 0: {
				this.data.currentOffer.slot.pop();
				this.data.selectedOffer.surchargeForExtraRoomSpace = 0;
				break;
			}

			case freePlaces === 0: {
				this.data.selectedOffer.surchargeForExtraRoomSpace = 0;
				break;
			}
		}
	}

	private subscribeForData(): void {
		forkJoin([
			from(this.fetchPromotions()),
			this.reservationSummaryApiService.getSellTransport(this.data.selectedOffer.term),
		]).subscribe(([promotions, stops]) => {
			this.promotions = promotions as Promotion[];
			this.stops = stops
				? (
						stops.map((stop) => ({
							stop: stop.id,
							address: stop.address,
							fees: { departure: stop.totalFee / 2, return: stop.totalFee / 2 },
							name: stop.location,
							transport: { departureId: stop.departureId, returnId: stop.returnId },
						})) as ChangeOfferStop[]
					).filter((item) => item.name)
				: [];

			this.stops.push(this.ownTransportStopFactory());
			this.slotsInitialization();
			this.servicesInitialization();
			if (this.data.selectedOffer.promotion) {
				this.selectPromotion(this.data.selectedOffer.promotion.id);
			}
			this.loading$.next(false);
		});
	}

	private assignInsurances() {
		const { insurance, defaultInsurance } = this.data.selectedOffer.product;

		if (!this.data.selectedOffer.insurance) {
			this.data.selectedOffer.insurance = defaultInsurance;
		}

		if (!this.data.selectedOffer.insurer) {
			this.data.selectedOffer.insurer = this.data.selectedOffer.product.insurer?.id;
		}

		const insurancesArray: Insurance[] = [this.data.selectedOffer.insurance];

		insurancesArray.push(this.data.selectedOffer.insurance?.isDefault ? insurance : defaultInsurance);

		this.insurances.set(insurancesArray.filter(Boolean));
		this.setInsurancePrice(this.data.selectedOffer.insurance);
	}

	private setInsurancePrice(insurance: Insurance): void {
		this.insurancePrice.set(
			!!insurance
				? this.data.currentOffer.slot[0].days * insurance.price * this.data.currentOffer.slot.length
				: 0,
		);

		this.data.currentOffer.slot.forEach((s) => {
			this.calculateNewPrices(s);
		});
	}

	private get transportKind() {
		const everySlotHasStop = this.data.currentOffer.slot.every(
			(v) => v.selectedStopDeparture.stop && v.selectedStopReturn.stop,
		);
		if (everySlotHasStop) {
			if (this.data.currentOffer.type === OfferChangeTypes.CONFIG) {
				return this.data.currentOffer.transportKind ?? TransportKind.COACH;
			}
			return this.data.selectedOffer.transportKind ?? TransportKind.COACH;
		}
		const isOwn = this.data.currentOffer.slot.every(
			(v) => !v.selectedStopDeparture.stop && !v.selectedStopReturn.stop,
		);
		if (isOwn) return TransportKind.WITHOUT;
		return TransportKind.MIX;
	}

	private get uniqueStops(): ChangeOfferStop[] {
		const uniqueStopsSet = new Set([
			...this.data.currentOffer.slot.flatMap((item) => [item.selectedStopDeparture, item.selectedStopReturn]),
		]);
		return Array.from(uniqueStopsSet).filter((value) => value.stop);
	}

	private get buildPromotionParams(): string {
		const offer = this.data.selectedOffer;
		const locations = offer.product.locations || [offer['product' as any].location];
		return this.urlBuilder
			.clearParams()
			.setBoards([offer.board.id])
			.setKinds([offer.product.productKind.id])
			.setLocations(locations.map((v) => v.id))
			.setStops(offer.stop.id ? [offer.stop.id] : [])
			.setProduct([offer.product.id])
			.setSellChannels(['office'])
			.buildParams();
	}

	private ownTransportStopFactory(): ChangeOfferStop {
		const isOwnTransportAvailable = this.priceSettingsOptions$
			.getValue()
			.find(
				(service) =>
					service.priceOption.name.toLocaleLowerCase() ===
					PriceSettingServicesNames.OWN_TRANSPORT.toLocaleLowerCase(),
			);

		return {
			address: '',
			fees: { departure: 0, return: 0 },
			name: isOwnTransportAvailable ? OWN_TRANSPORT_NAME : NO_TRANSPORT_NAME,
			stop: null,
			transport: { departureId: null, returnId: null },
		};
	}

	private createSlotToSendObj(slot: OfferChangeSlot): SlotOfferSend {
		return {
			catalogPrice: slot.newPrice + slot.newRoomTypePrice + slot.promotionValue,
			feeDueToCancellation: slot.feeDueToCancellation,
			id: null,
			kind: slot.kind,
			priceBoard: slot.newBoardPrice,
			priceTransportDeparture: slot.selectedStopDeparture.fees.departure,
			priceTransportReturn: slot.selectedStopReturn.fees.return,
			slotServices: slot.slotServices.filter((slot) => slot.active).map((item) => ({ service: item.service.id })),
			status: slot.status,
			stopDeparture: slot.selectedStopDeparture.stop ?? null,
			stopReturn: slot.selectedStopReturn.stop ?? null,
			transportDeparture: slot.selectedStopDeparture.transport.departureId ?? null,
			transportReturn: slot.selectedStopReturn.transport.returnId ?? null,
			userData: slot.userData && this.data.currentOffer.type !== OfferChangeTypes.ADD ? slot.userData.id : null,
			priceAdjustment: slot.priceAdjustment ?? null,
		};
	}

	private createReservationOfferObj(): ReservationOfferSend {
		return {
			board: this.data.selectedOffer.board.id,
			product: this.data.selectedOffer.product.id,
			promotion: this.data.selectedOffer.promotion ? [this.data.selectedOffer.promotion.id] : [],
			roomType: this.data.selectedOffer.roomType.id,
			slot: this.convertSlotToSend(),
			status: this.statusToSend,
			term: this.data.selectedOffer.term,
			transportKind: this.transportKind,
			surchargeFee: this.data.selectedOffer.surchargeFee,
			surchargeCurrency: this.data.selectedOffer.surchargeCurrency?.id ?? null,
			additionalRoomSizeCharge:
				this.additionalRoomSizeCharge * this.data.selectedOffer.nightsStay +
				this.data.selectedOffer.surchargeForExtraRoomSpace,
			insurance: this.data.selectedOffer.insurance?.isDefault === false,
		};
	}

	private createOfferChangeServiceObj(selectedService: FrontendOfferService, i: number): OfferChangeService {
		return {
			active: true,
			overwrittenPrice: null,
			countingMethod: selectedService.countingMethod,
			id: null,
			newPrice: this.calculateServicePrice(selectedService, i),
			service: { ...selectedService },
			optionName: '',
			price: 0,
			value: 0,
			kinds: [],
			isActive: true,
			description: '',
			code: '',
			count: '',
			method: '',
			methodValue: 0,
			currency: null,
		};
	}

	private setSlotPrice(slot: OfferChangeSlot): number {
		const getTransportFee = (stop: ChangeOfferStop, type: Transport): number => (stop ? stop.fees[type] : 0);

		const returnFee = getTransportFee(slot.selectedStopReturn, Transport.RETURN);
		const departureFee = getTransportFee(slot.selectedStopDeparture, Transport.DEPARTURE);

		return this.data.selectedOffer.catalogPrice + returnFee + departureFee - slot.promotionValue;
	}

	private servicesInitialization(): void {
		if (this.data.currentOffer) {
			this.data.currentOffer.selectedService = null;
		}
		const { offerService } = this.store.getters();
		this.offerServiceSubscription = offerService().subscribe((offerService) => {
			if (!offerService) {
				return;
			}
			this.offerServices = offerService.filter((v) => v.isActive);
			if (
				this.data.currentOffer.status === Status.CONFIRMED &&
				this.data.currentOffer.type === OfferChangeTypes.CHANGE
			)
				this.addChangeOfferService(this.data.surchargeService);
			this.data.currentOffer.slot.forEach((item) => {
				this.filterServices(this.offerServices, item);
				this.calculateNewPrices(item);
			});
		});
	}

	private slotsInitialization(): void {
		this.data.currentOffer?.slot.forEach((item) => {
			const transportDeparture = item.stopDeparture
				? {
						transport: item.transportDeparture,
						fee: item.stopDeparture.fee,
					}
				: null;

			const transportReturn = item.stopReturn
				? { transport: item.transportReturn, fee: item.stopReturn.fee }
				: null;

			item.selectedService = null;

			item.selectedStopReturn =
				this.findStopId(item.stopReturn?.stop.id) ??
				this.addMissingStop(item.stopReturn?.stop, transportDeparture, transportReturn);

			item.selectedStopDeparture =
				this.findStopId(item.stopDeparture?.stop.id) ??
				this.addMissingStop(item.stopDeparture?.stop, transportDeparture, transportReturn);

			item.priceAdjustment =
				this.data.currentOffer.type === OfferChangeTypes.CONFIG && item.priceAdjustment
					? item.priceAdjustment
					: 0;

			item.promotionValue = this.data.selectedOffer.promotionValue;
		});
		this.convertSlot();
		this.addMissingSlots();
	}

	private convertSlotToSend(): SlotOfferSend[] {
		const offerChangeSlots = deepClone<OfferChangeSlot[]>(this.data.currentOffer.slot);
		return offerChangeSlots
			.filter((slot) => slot.active)
			.map((slot: OfferChangeSlot) => this.createSlotToSendObj(slot));
	}

	private translateNights(): string {
		if (this.data.selectedOffer?.nightsStay === 0) return 'bez noclegu';
		if (this.data.selectedOffer?.nightsStay === 1) return 'noc';
		if (this.data.selectedOffer?.nightsStay > 1 && this.data.selectedOffer?.nightsStay < 5) return 'noclegi';
		return 'noclegów';
	}

	private filterServices(v: FrontendOfferService[], item: OfferChangeSlot) {
		if (!item.slotServices) {
			return;
		}
		item.servicesToAdd = v.filter(
			(val) =>
				!item.slotServices.map((el) => el.service).some((x) => x.id === val.id) && val.serviceType.id !== 13,
		);
	}

	private convertSlot(): void {
		this.data.currentOffer?.slot.forEach((slot) => {
			slot.newPrice = this.setSlotPrice(slot);
			slot.newBoardPrice = this.data.selectedOffer?.board.price;
			if (typeof slot.newRoomTypePrice !== 'number') {
				slot.newRoomTypePrice = 0;
			}
			slot.slotServices.forEach((item) => {
				item.newPrice = item.price;
				item.active = true;
			});
			slot.active = true;
			this.calculateNewPrices(slot);
		});
	}

	private async fetchPromotions(): Promise<Promotion[]> {
		const { getPromotions, getPromotion } = this.reservationSummaryApiService.actions();
		const promotions = await getPromotions(this.buildPromotionParams);
		const offerPromotion = this.data.selectedOffer.promotion;
		if (!offerPromotion) return promotions;
		if (!promotions.find((value) => value.id === offerPromotion.id)) {
			const promotion = await getPromotion(offerPromotion.id);
			return [promotion, ...promotions];
		}
		return promotions;
	}

	private findStopId(stop: number): ChangeOfferStop {
		if (stop === undefined) {
			return this.ownTransportStopFactory();
		}
		const stopId = this.data.currentOffer.type === OfferChangeTypes.CONFIG ? stop : this.data.selectedOffer.stop.id;
		const selectedStop = this.stops.find((v) => v.stop === stopId);
		const currentSlot = this.stops.find((v) => v.stop === stop);
		return selectedStop ?? currentSlot;
	}

	private addMissingStop(
		stop: Stop,
		transportDeparture: { transport: number; fee: number },
		transportReturn: { transport: number; fee: number },
	): OfferChangeStop {
		if (!stop || this.stops.some((v) => v.stop === stop.id)) {
			return;
		}
		const offerChangeStop: OfferChangeStop = {
			stop: stop.id,
			name: stop.location.name,
			address: stop.address,
			fees: {
				departure: transportDeparture?.fee ?? 0,
				return: transportReturn?.fee ?? 0,
			},
			transport: {
				departureId: transportDeparture?.transport ?? null,
				returnId: transportReturn?.transport ?? null,
			},
		};
		this.stops.push(offerChangeStop);
		return offerChangeStop;
	}

	private addMissingSlots(): void {
		const totalParticipants =
			this.data.selectedOffer.participants.adults + this.data.selectedOffer.participants.children;

		if (this.data.currentOffer.slot.length > totalParticipants && totalParticipants) {
			for (let i = this.data.currentOffer.slot.length; i > totalParticipants; i--) {
				this.data.currentOffer.slot.pop();
			}
		} else {
			for (let i = this.data.currentOffer.slot.length; i < totalParticipants; i++) {
				const newSlot = { ...this.data.currentOffer.slot[0] };
				newSlot.slotServices = [];
				newSlot.servicesToAdd = this.offerServices;
				newSlot.userData = null;
				this.data.currentOffer.slot.push(newSlot);
			}
		}
	}

	private addChangeOfferService(service: FrontendOfferService): void {
		this.data.currentOffer.slot.forEach((slot, i) => {
			if (!slot.slotServices.find((v) => v.service.name === service.name)) {
				this.addService(i, service);
			}
		});
	}

	private setAdditionalRoomSizeCharge(activeSlots: number): number {
		const inactiveCount = this.data.currentOffer.slot.length - activeSlots;
		return inactiveCount * this.data.selectedOffer.board.additionalRoomSizeCharge;
	}

	private showAvailabilityWarningMessage(isRoomAvailable: boolean, isTransportAvailable: boolean) {
		let warningMessage = '';
		switch (true) {
			case !isRoomAvailable && !isTransportAvailable:
				warningMessage = 'Wybrany pokój i transport, w tym terminie nie są już dostępne';
				break;
			case !isRoomAvailable:
				warningMessage = 'Wybrany pokój, w tym terminie nie jest już dostępny';
				break;
			case !isTransportAvailable:
				warningMessage = 'Wybrany transport, w tym terminie nie jest już dostępny';
				break;
		}

		this.infobox.warning(warningMessage);
	}

	private getServiceSlotPrice(slot: OfferChangeSlot): number {
		return this.data.currentOffer.type === OfferChangeTypes.CONFIG
			? slot.newPrice + slot.newBoardPrice + slot.newRoomTypePrice
			: this.data.selectedOffer.fullPrice;
	}

	private calculateServicePrice(service: FrontendOfferService, i: number): number {
		let serviceValue = 0;
		let serviceMultiplier = 1;
		const currentSlot = this.data.currentOffer.slot[i];
		const slotPrice = this.getServiceSlotPrice(currentSlot);
		switch (service.countingMethod) {
			case ServiceCountingMethods.CUSTOM:
				const slotPriceWithSumOfServices =
					slotPrice + this.sumOfServicesExcludingCancellationInsurance(currentSlot);
				serviceValue = this.getServiceValue(slotPriceWithSumOfServices, service.methodValue);
				break;
			case ServiceCountingMethods.VALUE:
				serviceValue = service.methodValue;
				break;
			case ServiceCountingMethods.PERMILLE:
				if (service.serviceType.name === CANCELLATION_INSURANCE_NAME) {
					// BE: sum_of_services_excluding_cancellation_insurance() + offer_price
					serviceValue = this.getServiceValue(
						slotPrice + this.sumOfServicesExcludingCancellationInsurance(currentSlot),
						service.methodValue,
					);
				} else {
					serviceValue = this.getServiceValue(
						slotPrice - this.getSlotTransportPrice(currentSlot),
						service.methodValue,
					);
				}
				break;
			default:
				throw new Error(`Unsupported counting method: ${service.countingMethod}`);
		}

		switch (service.dependsOn) {
			case ServiceDependsOn.SLOT:
				serviceMultiplier = 1;
				break;
			case ServiceDependsOn.DAY:
				serviceMultiplier = this.data.currentOffer.slot[i].days;
				break;
			case ServiceDependsOn.NIGHTS_STAY:
				serviceMultiplier = this.data.selectedOffer.nightsStay;
				break;
			default:
				throw new Error(`Unsupported service dependency: ${service.dependsOn}`);
		}
		return serviceMultiplier * serviceValue;
	}

	private getServiceValue(value: number, methodValue: number): number {
		return Math.floor((value * methodValue) / 10000);
	}

	private sumOfServicesExcludingCancellationInsurance(slot: OfferChangeSlot): number {
		return slot.slotServices
			.filter((value) => value.service.serviceType.name !== CANCELLATION_INSURANCE_NAME && value.active)
			.reduce((accumulator, currentValue) => accumulator + currentValue.newPrice, 0);
	}

	private calculatePromotionValue(promotion: Promotion): number {
		let sumOfPromotionValue = 0;
		this.data.currentOffer.slot.forEach((slot) => {
			if (!promotion) {
				return;
			}

			if (promotion.method === PaymentTermCountKind.PERCENTAGE) {
				const transportPrice = this.getSlotTransportPrice(slot);
				const isOwnTransport = this.isSlotOwnTransport(slot);
				const includeTransportInPromo = shouldIncludeTransportInPromo(
					this.getOfferTransportKindForPromo(),
					isOwnTransport,
				);
				const promotableBase =
					this.data.selectedOffer.catalogPrice +
					slot.newRoomTypePrice +
					slot.newBoardPrice +
					(includeTransportInPromo ? transportPrice : 0);

				slot.promotionValue = calcPercentagePromotionValue(promotableBase, promotion.methodValue);
			} else {
				slot.promotionValue = promotion.methodValue;
			}

			sumOfPromotionValue += slot.promotionValue;
		});
		return sumOfPromotionValue;
	}

	private getOfferTransportKindForPromo(): string {
		if (this.data.currentOffer.type === OfferChangeTypes.CONFIG) {
			return this.data.currentOffer.transportKind ?? TransportKind.COACH;
		}

		return this.data.selectedOffer.transportKind ?? TransportKind.COACH;
	}

	private getSlotTransportPrice(slot: OfferChangeSlot): number {
		const departureFee = slot.selectedStopDeparture?.fees?.departure ?? 0;
		const returnFee = slot.selectedStopReturn?.fees?.return ?? 0;

		return departureFee + returnFee;
	}

	private isSlotOwnTransport(slot: OfferChangeSlot): boolean {
		return !slot.selectedStopDeparture?.stop && !slot.selectedStopReturn?.stop;
	}

	// move the cancellation insurance to the end of the array,
	// because it's price is calculated taking into account the rest of the services and must be calculated at the end,
	// after calculating the price, I restore the array to their original state
	private moveCancellationInsuranceToTheEndOfArray(slotServices: OfferChangeService[], callback: () => void) {
		const cancellationInsuranceIndex: number = slotServices.findIndex(
			(value) => value.service.serviceType.name === CANCELLATION_INSURANCE_NAME,
		);
		if (cancellationInsuranceIndex !== -1) {
			slotServices.push(slotServices.splice(cancellationInsuranceIndex, 1)[0]);
		}
		callback();
		if (cancellationInsuranceIndex !== -1) {
			slotServices.splice(cancellationInsuranceIndex, 0, slotServices.pop()!);
		}
	}

	private calculateTotalSlotServicePrice(slot: OfferChangeSlot): number {
		let servicesPrice = 0;
		slot.slotServices.forEach((slotService) => {
			const service = this.offerServices.find((service) => service.id === slotService.service.id);
			const currentSlotIndex = this.data.currentOffer.slot.findIndex((object: OfferChangeSlot) => {
				return object.id === slot.id;
			});
			const calculatedNewPrice = () => {
				if (service) {
					return this.calculateServicePrice(service, currentSlotIndex);
				}
				return slotService.price;
			};
			slotService.newPrice = calculatedNewPrice();
			if (slotService.active) servicesPrice += slotService.newPrice;
		});
		return servicesPrice;
	}

	private calculateOfferTotalPrice(): void {
		let totalPrice = 0;
		const activeItems = this.data.currentOffer.slot.filter((item) => item.active);
		if (activeItems.length) {
			totalPrice = activeItems.map((v) => v.newTotalPrice).reduce((a, b) => a + b);
		}
		if (this.data.currentOffer.oskarFault) {
			totalPrice = totalPrice - this.data.currentOffer.term.gratis;
		}
		this.additionalRoomSizeCharge = this.setAdditionalRoomSizeCharge(activeItems.length);

		const surchargeForExtraRoomSpace = this.data.selectedOffer?.surchargeForExtraRoomSpace ?? 0;

		this.data.currentOffer.newTotalPrice =
			totalPrice + this.additionalRoomSizeCharge + surchargeForExtraRoomSpace + this.insurancePrice();
	}

	private triggerOfferCreate(reservationOffer: ReservationOfferSend): void {
		const { createOffer } = this.reservationSummaryApiService.actions();
		createOffer(this.reservationId, reservationOffer).then(() => {
			this.successfulRequestActions('Pomyślnie dodano pokój');
		});
	}

	private async triggerOfferChange(reservationOffer: ReservationOfferSend): Promise<void> {
		const { cancelReservationOffer, createOffer, patchOffer, patchOfferSlot } =
			this.reservationSummaryApiService.actions();
		const cancelOffer = {
			frontendStatus: Status.REJECTED_CLIENT,
			offerCancellationReason: [
				{
					cancellationReason: 'ZO',
					reservation: null,
					reservationOffer: this.data.currentOffer.id,
					reservationOfferSlot: null,
				},
			],
		};

		try {
			await cancelReservationOffer(this.data.currentOffer.id, cancelOffer);
			await createOffer(this.reservationId, reservationOffer);
			this.successfulRequestActions('Pomyślnie zmieniono ofertę');
		} catch (error) {
			await patchOffer({ status: reservationOffer.status }, this.data.currentOffer.id);
			await Promise.all(this.data.currentOffer.slot.map((s) => patchOfferSlot(s.id, { status: s.status })));
			throw new Error(error);
		}
	}

	private successfulRequestActions(message: string): void {
		this.dialog.closeAll();
		this.reservationSummaryApiService.actions().getReservation(this.reservationId);
		this.infobox.success(message);
		this.showPaymentTermModal();
		this.loader.hide();
	}

	private triggerOfferConfig(reservationOffer: ReservationOfferSend): void {
		this.loader.show();

		const offerToSend: ReservationOfferSend = {
			...reservationOffer,
			status: this.statusToSend,
			transportKind: this.transportKind,
			promotion: this.data.selectedOffer.promotion ? [this.data.selectedOffer.promotion.id] : [],
			slot: reservationOffer.slot.map((slot, index) => {
				slot.id = this.data.currentOffer.slot[index].id;
				return slot;
			}),
			surchargeCurrency: this.data.selectedOffer.surchargeCurrency as unknown as number,
		};

		this.reservationSummaryApiService
			.updateReservationOffer(this.reservationId, this.data.currentOffer.id, offerToSend)
			.pipe(
				catchError((error) => {
					if (error.status === StatusCode.CONFLICT_RESPONSE_ERROR) {
						this.loader.hide();
						return this.forceOfferChange(offerToSend);
					} else {
						return of(null);
					}
				}),
				filter((result) => result !== null),
				finalize(() => this.loader.hide()),
			)
			.subscribe(() => {
				this.successfulRequestActions('Pomyślnie zaktualizowano ofertę');
			});
	}

	private forceOfferChange(offerToSend): Observable<void> {
		return this.dialog
			.open(ForceTransportDialogComponent, {
				width: '450px',
			})
			.afterClosed()
			.pipe(
				switchMap((confirmed: boolean) => {
					if (confirmed) {
						return this.reservationSummaryApiService.updateReservationOffer(
							this.reservationId,
							this.data.currentOffer.id,
							offerToSend,
							confirmed,
						);
					} else {
						return of(null);
					}
				}),
			);
	}

	private confirmOfferChange(): void {
		this.loader.show();
		const reservationOffer = this.createReservationOfferObj();
		switch (this.data.currentOffer.type) {
			case OfferChangeTypes.ADD:
				reservationOffer.slot = this.applyOKStatusToSlots(reservationOffer);
				this.triggerOfferCreate(reservationOffer);
				break;
			case OfferChangeTypes.CHANGE:
				this.triggerOfferChange(reservationOffer);
				break;
			case OfferChangeTypes.CONFIG:
				this.triggerOfferConfig(reservationOffer);
				break;
			default:
				this.loader.hide();
				throw new Error('Unexpected offer change type!');
		}
	}

	private get statusToSend(): Status {
		if (this.data.currentOffer.type === OfferChangeTypes.ADD) {
			return Status.OPTION;
		}

		if (this.data.currentOffer.status === Status.CONFIRMED && this.data.reservationStatus === Status.CONFIRMED) {
			return Status.CONFIRMED;
		}
		return Status.OPTION;
	}

	private showPaymentTermModal(): void {
		const { paymentTerm } = this.store.getters();
		paymentTerm()
			.pipe(
				take(1),
				filter((paymentTerm) => paymentTerm.some((v) => v.countKind === 'V')),
			)
			.subscribe(() => {
				this.infoboxService.warning(
					'W terminach płatności znajdowała się stała wartość!<br>Upewnij się, że suma wprowadzonych terminów jest zgodna z kwotą rezerwacji.',
				);
			});
	}

	private checkIfDepartureHasChanged(): boolean {
		const originalDepartureIds = this.data.currentOffer.slot
			.map((v) => v.stopDeparture?.stop?.id)
			.filter(Boolean)
			.sort();
		const currentDepartureIds = this.data.currentOffer.slot
			.map((v) => v.selectedStopDeparture?.stop)
			.filter(Boolean)
			.sort();

		return !deepCompare(originalDepartureIds, currentDepartureIds);
	}

	private applyOKStatusToSlots(reservationOffer: ReservationOfferSend): ReservationOfferSend['slot'] {
		return reservationOffer.slot.map((s) => ({
			...s,
			status: Status.OPTION,
		}));
	}
}
